import React, { useCallback, useEffect, useState } from 'react';
import { Spin, Upload, Input, Button, Image, Modal, DatePicker } from 'antd';
import { EyeOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import toast from '../toast';
import { DocumentationApiClient, DocumentationBucket } from '../../services/documentationApiClient';
import uploadImageLogo from '../../assets/images/image-upload.png';
import constants from '../../config/constants';
import '../creategroupmodal/attachment-upload-modal.css';
import './documentsPanel.css';

interface DocFile {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  has_file?: boolean;
}

interface ModelEntry {
  id: string;
  file: DocFile | null;
  text?: string;
  created_at?: number;
  creator_name?: string;
}

interface DocumentEntry {
  id: string;
  file: DocFile | null;
  text: string;
  created_at: number;
  created_by?: string;
  creator_name?: string;
}

interface Documentation {
  model: ModelEntry[];
  before_install: DocumentEntry[];
  after_install: DocumentEntry[];
  miscellaneous: DocumentEntry[];
}

const EMPTY_DOCS: Documentation = {
  model: [],
  before_install: [],
  after_install: [],
  miscellaneous: [],
};

const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp';
const MISC_ACCEPT = '.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx';

const isImage = (mimeType?: string) => !!mimeType && mimeType.startsWith('image/');

const formatDocDate = (timestamp?: number) => {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString();
};

const disableFutureDate = (current: any) => current && current > moment().endOf('day');

const disableFutureTime = (current: any) => {
  if (!current || !current.isSame(moment(), 'day')) return {};
  const now = moment();
  const currentHour = now.hour();
  const currentMinute = now.minute();
  return {
    disabledHours: () => Array.from({ length: 24 }, (_, h) => h).filter((h) => h > currentHour),
    disabledMinutes: (selectedHour: number) => {
      if (selectedHour !== currentHour) return [];
      return Array.from({ length: 60 }, (_, m) => m).filter((m) => m > currentMinute);
    },
  };
};

const MAX_UPLOAD_FILES = 5;

/** In-memory blob URLs so thumbnails / View / Download don't re-download from S3. */
const blobUrlCache = new Map<string, string>();

/** Fetch file via authenticated API proxy and return a local blob URL (never S3). */
async function fetchProxiedBlobUrl(
  fetcher: () => Promise<{ data: Blob }>,
): Promise<string> {
  const res = await fetcher();
  return URL.createObjectURL(res.data);
}

async function getCachedBlobUrl(
  cacheKey: string,
  fetcher: () => Promise<{ data: Blob }>,
): Promise<string> {
  const cached = blobUrlCache.get(cacheKey);
  if (cached) return cached;
  const url = await fetchProxiedBlobUrl(fetcher);
  blobUrlCache.set(cacheKey, url);
  return url;
}

function downloadFromBlobUrl(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function invalidateBlobCache(cacheKey: string) {
  const url = blobUrlCache.get(cacheKey);
  if (url) {
    URL.revokeObjectURL(url);
    blobUrlCache.delete(cacheKey);
  }
}

/** Loads an image thumbnail through the auth proxy so the browser never sees S3. */
const ProxiedImage: React.FC<{
  cacheKey: string;
  load: () => Promise<{ data: Blob }>;
  alt: string;
  className?: string;
}> = ({ cacheKey, load, alt, className }) => {
  const [url, setUrl] = useState<string | null>(() => blobUrlCache.get(cacheKey) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cached = blobUrlCache.get(cacheKey);
    if (cached) {
      setUrl(cached);
      setFailed(false);
      return;
    }
    setUrl(null);
    setFailed(false);
    (async () => {
      try {
        const objectUrl = await getCachedBlobUrl(cacheKey, load);
        if (!cancelled) setUrl(objectUrl);
      } catch {
        if (!cancelled) {
          setUrl(null);
          setFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  if (failed) return <div className="dp-media-missing">Failed to load</div>;
  if (!url) return <div className="dp-media-missing">Loading…</div>;
  return <img className={className} src={url} alt={alt} />;
};

interface Props {
  entityId: string;
  api: DocumentationApiClient;
  disabled?: boolean;
  emptyModelLabel?: string;
  /** Called after a successful mutation refresh (add/edit/delete). */
  onChanged?: () => void;
}

/**
 * Model / Images (Before + After, side by side) / Misc documentation panel.
 * Shared between the sensorbox modal (per-component docs) and the Asset
 * Details modal (asset-wide bucket) — which entity it talks to is entirely
 * determined by the `api` + `entityId` props.
 */
const DocumentsPanel: React.FC<Props> = ({ entityId, api, disabled, emptyModelLabel, onChanged }) => {
  const [subTab, setSubTab] = useState<'model' | 'images' | 'misc'>('model');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [docs, setDocs] = useState<Documentation>(EMPTY_DOCS);

  const fetchAll = useCallback(async () => {
    if (!entityId) return;
    try {
      setLoading(true);
      setLoadError(null);
      const docsRes = await api.getDocumentation(entityId);
      setDocs(docsRes?.data?.result || EMPTY_DOCS);
    } catch (error: any) {
      const message = error?.response?.data?.detail || 'Failed to load documentation';
      setDocs(EMPTY_DOCS);
      setLoadError(typeof message === 'string' ? message : 'Failed to load documentation');
      toast.error(typeof message === 'string' ? message : 'Failed to load documentation');
    } finally {
      setLoading(false);
    }
  }, [entityId, api]);

  const handleChanged = useCallback(async () => {
    await fetchAll();
    onChanged?.();
  }, [fetchAll, onChanged]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <Spin spinning={loading}>
      <div className="documents-panel">
        <div className="dp-subtabs">
          <button
            type="button"
            className={`dp-subtab ${subTab === 'model' ? 'active' : ''}`}
            onClick={() => setSubTab('model')}
          >
            Model
          </button>
          <button
            type="button"
            className={`dp-subtab ${subTab === 'images' ? 'active' : ''}`}
            onClick={() => setSubTab('images')}
          >
            Images
          </button>
          <button
            type="button"
            className={`dp-subtab ${subTab === 'misc' ? 'active' : ''}`}
            onClick={() => setSubTab('misc')}
          >
            Misc
          </button>
        </div>

        {loadError ? (
          <div className="dp-empty dp-error">
            <div>{loadError}</div>
            <button type="button" className="dp-retry" onClick={() => fetchAll()}>
              Retry
            </button>
          </div>
        ) : (
          <>
            {subTab === 'model' && (
              <ModelSection
                entityId={entityId}
                api={api}
                entries={docs.model}
                disabled={!!disabled}
                onChanged={handleChanged}
                emptyLabel={emptyModelLabel}
              />
            )}
            {subTab === 'images' && (
              <ImagesSection
                entityId={entityId}
                api={api}
                before={docs.before_install}
                after={docs.after_install}
                disabled={!!disabled}
                onChanged={handleChanged}
              />
            )}
            {subTab === 'misc' && (
              <MiscSection
                entityId={entityId}
                api={api}
                entries={docs.miscellaneous}
                disabled={!!disabled}
                onChanged={handleChanged}
              />
            )}
          </>
        )}
      </div>
    </Spin>
  );
};

export default DocumentsPanel;

// ============================== Model section (images only) ==============================

const ModelSection: React.FC<{
  entityId: string;
  api: DocumentationApiClient;
  entries: ModelEntry[];
  disabled: boolean;
  onChanged: () => void;
  emptyLabel?: string;
}> = ({ entityId, api, entries, disabled, onChanged, emptyLabel }) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const cacheKeyFor = (entryId: string) => `model:${entityId}:${entryId}`;

  const handleUpload = async (files: File[], text: string, createdAt: number) => {
    try {
      setUploading(true);
      for (const file of files) {
        await api.addModelEntry(entityId, { file, text, createdAt });
      }
      setUploadOpen(false);
      toast.success(files.length > 1 ? 'Images uploaded' : 'Image uploaded');
      onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (entryId: string) => {
    Modal.confirm({
      title: 'Delete image',
      content: 'Are you sure you want to delete this model image?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      zIndex: 10000000,
      maskClosable: false,
      onOk: async () => {
        try {
          await api.deleteModelEntry(entityId, entryId);
          invalidateBlobCache(cacheKeyFor(entryId));
          toast.success('Deleted');
          onChanged();
        } catch (error: any) {
          toast.error(error?.response?.data?.detail || 'Delete failed');
        }
      },
    });
  };

  const handleView = async (entry: ModelEntry) => {
    try {
      setBusyAction(`view:${entry.id}`);
      const url = await getCachedBlobUrl(cacheKeyFor(entry.id), () =>
        api.getModelFileContent(entityId, entry.id, 'inline')
      );
      setPreviewUrl(url);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to open file');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDownload = async (entry: ModelEntry) => {
    try {
      setBusyAction(`download:${entry.id}`);
      const url = await getCachedBlobUrl(cacheKeyFor(entry.id), () =>
        api.getModelFileContent(entityId, entry.id, 'inline')
      );
      downloadFromBlobUrl(url, entry.file?.originalName || entry.file?.filename || 'download');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to download file');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div>
      {entries.length === 0 && (
        <div className="dp-empty">{emptyLabel || 'No model images added yet.'}</div>
      )}
      <div className="dp-image-list dp-image-list-grid">
        {entries.map((entry) => (
          <div className="dp-media-card" key={entry.id}>
            <div
              className="dp-media-frame dp-media-frame-lg"
              onMouseEnter={() => setHoveredId(entry.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {entry.file?.has_file !== false && entry.file?.id ? (
                <ProxiedImage
                  cacheKey={cacheKeyFor(entry.id)}
                  load={() => api.getModelFileContent(entityId, entry.id, 'inline')}
                  alt={entry.file.originalName || 'Model image'}
                />
              ) : (
                <div className="dp-media-missing">No preview</div>
              )}
              {hoveredId === entry.id && entry.file && (
                <div className="dp-media-overlay">
                  <Button
                    size="small"
                    type="primary"
                    icon={<EyeOutlined />}
                    loading={busyAction === `view:${entry.id}`}
                    onClick={() => handleView(entry)}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={busyAction === `download:${entry.id}`}
                    onClick={() => handleDownload(entry)}
                  >
                    Download
                  </Button>
                  {!disabled && (
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(entry.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="dp-media-note">
              <p className="dp-media-note-text">{entry.text || ''}</p>
              <small className="text-muted">
                By: {entry.creator_name || 'N/A'} • {formatDocDate(entry.created_at)}
              </small>
            </div>
          </div>
        ))}
      </div>
      {!disabled && (
        <button type="button" className="btn btn-primary dp-block-btn" onClick={() => setUploadOpen(true)}>
          + Add Model Image
        </button>
      )}
      <DocumentationUploadModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
        loading={uploading}
        accept={IMAGE_ACCEPT}
        imagesOnly
        title="Add Model Images & Notes"
      />
      {previewUrl && (
        <Image
          src={previewUrl}
          style={{ display: 'none' }}
          preview={{
            visible: !!previewUrl,
            onVisibleChange: (visible) => {
              if (!visible) setPreviewUrl(null);
            },
          }}
        />
      )}
    </div>
  );
};


// ============================== Images section (Before / After, side by side) ==============================

const ImagesSection: React.FC<{
  entityId: string;
  api: DocumentationApiClient;
  before: DocumentEntry[];
  after: DocumentEntry[];
  disabled: boolean;
  onChanged: () => void;
}> = ({ entityId, api, before, after, disabled, onChanged }) => {
  return (
    <div className="dp-images-grid">
      <ImageColumn title="Before Install" bucket="before_install" entityId={entityId} api={api} entries={before} disabled={disabled} onChanged={onChanged} />
      <ImageColumn title="After Install" bucket="after_install" entityId={entityId} api={api} entries={after} disabled={disabled} onChanged={onChanged} />
    </div>
  );
};

const ImageColumn: React.FC<{
  title: string;
  bucket: DocumentationBucket;
  entityId: string;
  api: DocumentationApiClient;
  entries: DocumentEntry[];
  disabled: boolean;
  onChanged: () => void;
}> = ({ title, bucket, entityId, api, entries, disabled, onChanged }) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const cacheKeyFor = (entryId: string) => `${bucket}:${entityId}:${entryId}`;

  const handleUpload = async (files: File[], text: string, createdAt: number) => {
    try {
      setUploading(true);
      await api.addBucketFiles(entityId, bucket, files, text, createdAt);
      setUploadOpen(false);
      toast.success('Photos uploaded');
      onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (entryId: string) => {
    Modal.confirm({
      title: 'Delete image',
      content: 'Are you sure you want to delete this image?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      zIndex: 10000000,
      maskClosable: false,
      onOk: async () => {
        try {
          await api.deleteBucketFile(entityId, bucket, entryId);
          invalidateBlobCache(cacheKeyFor(entryId));
          toast.success('Deleted');
          onChanged();
        } catch (error: any) {
          toast.error(error?.response?.data?.detail || 'Delete failed');
        }
      },
    });
  };

  const handleView = async (entry: DocumentEntry) => {
    try {
      setBusyAction(`view:${entry.id}`);
      const url = await getCachedBlobUrl(cacheKeyFor(entry.id), () =>
        api.getBucketFileContent(entityId, bucket, entry.id, 'inline')
      );
      setPreviewUrl(url);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to open file');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDownload = async (entry: DocumentEntry) => {
    try {
      setBusyAction(`download:${entry.id}`);
      const url = await getCachedBlobUrl(cacheKeyFor(entry.id), () =>
        api.getBucketFileContent(entityId, bucket, entry.id, 'inline')
      );
      downloadFromBlobUrl(url, entry.file?.originalName || entry.file?.filename || 'download');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to download file');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="dp-image-column">
      <div className="dp-image-column-title">{title} <span className="dp-count">({entries.length})</span></div>
      <div className="dp-image-list">
        {entries.length === 0 && <div className="dp-empty">No photos yet</div>}
        {entries.map((entry) => (
          <div className="dp-media-card" key={entry.id}>
            <div
              className="dp-media-frame"
              onMouseEnter={() => setHoveredId(entry.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {entry.file ? (
                <ProxiedImage
                  cacheKey={cacheKeyFor(entry.id)}
                  load={() => api.getBucketFileContent(entityId, bucket, entry.id, 'inline')}
                  alt={entry.file.originalName || title}
                />
              ) : (
                <div className="dp-media-missing">No preview</div>
              )}
              {hoveredId === entry.id && entry.file && (
                <div className="dp-media-overlay">
                  <Button
                    size="small"
                    type="primary"
                    icon={<EyeOutlined />}
                    loading={busyAction === `view:${entry.id}`}
                    onClick={() => handleView(entry)}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={busyAction === `download:${entry.id}`}
                    onClick={() => handleDownload(entry)}
                  >
                    Download
                  </Button>
                  {!disabled && (
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(entry.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="dp-media-note">
              <p className="dp-media-note-text">{entry.text || ''}</p>
              <small className="text-muted">
                By: {entry.creator_name || 'N/A'} • {formatDocDate(entry.created_at)}
              </small>
            </div>
          </div>
        ))}
      </div>
      {!disabled && (
        <button type="button" className="btn btn-primary dp-block-btn" onClick={() => setUploadOpen(true)}>
          + Add {title} Image
        </button>
      )}
      <DocumentationUploadModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
        loading={uploading}
        accept={IMAGE_ACCEPT}
        imagesOnly
        title={`Add ${title} Photos & Notes`}
      />
      {previewUrl && (
        <Image
          src={previewUrl}
          style={{ display: 'none' }}
          preview={{
            visible: !!previewUrl,
            onVisibleChange: (visible) => {
              if (!visible) setPreviewUrl(null);
            },
          }}
        />
      )}
    </div>
  );
};

// ============================== Misc section ==============================

const MiscSection: React.FC<{
  entityId: string;
  api: DocumentationApiClient;
  entries: DocumentEntry[];
  disabled: boolean;
  onChanged: () => void;
}> = ({ entityId, api, entries, disabled, onChanged }) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const cacheKeyFor = (entryId: string) => `miscellaneous:${entityId}:${entryId}`;

  const handleUpload = async (files: File[], text: string, createdAt: number) => {
    try {
      setUploading(true);
      await api.addBucketFiles(entityId, 'miscellaneous', files, text, createdAt);
      setUploadOpen(false);
      toast.success('Files uploaded');
      onChanged();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (entryId: string) => {
    Modal.confirm({
      title: 'Delete file',
      content: 'Are you sure you want to delete this file?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      zIndex: 10000000,
      maskClosable: false,
      onOk: async () => {
        try {
          await api.deleteBucketFile(entityId, 'miscellaneous', entryId);
          invalidateBlobCache(cacheKeyFor(entryId));
          toast.success('Deleted');
          onChanged();
        } catch (error: any) {
          toast.error(error?.response?.data?.detail || 'Delete failed');
        }
      },
    });
  };

  const handleView = async (entry: DocumentEntry) => {
    try {
      setBusyAction(`view:${entry.id}`);
      const url = await getCachedBlobUrl(cacheKeyFor(entry.id), () =>
        api.getBucketFileContent(entityId, 'miscellaneous', entry.id, 'inline')
      );
      if (isImage(entry.file?.mimeType)) {
        setPreviewUrl(url);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to open file');
    } finally {
      setBusyAction(null);
    }
  };

  const handleDownload = async (entry: DocumentEntry) => {
    try {
      setBusyAction(`download:${entry.id}`);
      const url = await getCachedBlobUrl(cacheKeyFor(entry.id), () =>
        api.getBucketFileContent(entityId, 'miscellaneous', entry.id, 'inline')
      );
      downloadFromBlobUrl(url, entry.file?.originalName || entry.file?.filename || 'download');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to download file');
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div>
      {entries.length === 0 && <div className="dp-empty">No files added yet</div>}
      <div className="dp-image-list">
        {entries.map((entry) => {
          const image = isImage(entry.file?.mimeType);
          return (
            <div className="dp-media-card" key={entry.id}>
              {image && entry.file ? (
                <div
                  className="dp-media-frame"
                  onMouseEnter={() => setHoveredId(entry.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <ProxiedImage
                    cacheKey={cacheKeyFor(entry.id)}
                    load={() => api.getBucketFileContent(entityId, 'miscellaneous', entry.id, 'inline')}
                    alt={entry.file.originalName}
                  />
                  {hoveredId === entry.id && (
                    <div className="dp-media-overlay">
                      <Button
                        size="small"
                        type="primary"
                        icon={<EyeOutlined />}
                        loading={busyAction === `view:${entry.id}`}
                        onClick={() => handleView(entry)}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        loading={busyAction === `download:${entry.id}`}
                        onClick={() => handleDownload(entry)}
                      >
                        Download
                      </Button>
                      {!disabled && (
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete(entry.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="dp-misc-doc-row">
                  <div className="dp-misc-thumb dp-misc-doc">📄</div>
                  <div className="dp-misc-info">
                    <div className="dp-misc-filename">{entry.file?.originalName || 'Document'}</div>
                    <div className="dp-misc-actions">
                      <Button
                        size="small"
                        type="primary"
                        icon={<EyeOutlined />}
                        loading={busyAction === `view:${entry.id}`}
                        onClick={() => handleView(entry)}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        loading={busyAction === `download:${entry.id}`}
                        onClick={() => handleDownload(entry)}
                      >
                        Download
                      </Button>
                      {!disabled && (
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(entry.id)}>
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="dp-media-note">
                <p className="dp-media-note-text">{entry.text || ''}</p>
                <small className="text-muted">
                  By: {entry.creator_name || 'N/A'} • {formatDocDate(entry.created_at)}
                </small>
              </div>
            </div>
          );
        })}
      </div>
      {!disabled && (
        <button type="button" className="btn btn-primary dp-block-btn" onClick={() => setUploadOpen(true)}>
          + Add image or document
        </button>
      )}
      <DocumentationUploadModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
        loading={uploading}
        accept={MISC_ACCEPT}
        imagesOnly={false}
        title="Add Attachments & Notes"
      />
      {previewUrl && (
        <Image
          src={previewUrl}
          style={{ display: 'none' }}
          preview={{
            visible: !!previewUrl,
            onVisibleChange: (visible) => {
              if (!visible) setPreviewUrl(null);
            },
          }}
        />
      )}
    </div>
  );
};

// ============================== Shared upload modal (matches Asset attachment UX) ==============================

const DocumentationUploadModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[], text: string, createdAt: number) => Promise<void>;
  loading: boolean;
  accept: string;
  imagesOnly: boolean;
  title: string;
}> = ({ isOpen, onClose, onUpload, loading, accept, imagesOnly, title }) => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [noteValue, setNoteValue] = useState('');
  const [createdAt, setCreatedAt] = useState<any>(() => moment());

  useEffect(() => {
    if (!isOpen) {
      setFileList([]);
      setNoteValue('');
      return;
    }
    setCreatedAt(moment());
  }, [isOpen]);

  if (!isOpen) return null;

  const beforeUploadValidation = (file: File) => {
    if (fileList.length >= MAX_UPLOAD_FILES) {
      toast.error(`Maximum of ${MAX_UPLOAD_FILES} files allowed for a description`);
      return Upload.LIST_IGNORE;
    }
    if (imagesOnly) {
      const ok = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type);
      if (!ok) {
        toast.error('Only jpeg, jpg, png, or webp images are allowed');
        return Upload.LIST_IGNORE;
      }
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File exceeds the 10 MB limit');
      return Upload.LIST_IGNORE;
    }
    return false; // prevent auto-upload; keep in list
  };

  const handleSubmit = async () => {
    const files = fileList.map((f) => f.originFileObj).filter(Boolean) as File[];
    if (files.length === 0 && !noteValue.trim()) return;
    if (files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }
    if (!createdAt) {
      toast.error('Please select a created date');
      return;
    }
    if (createdAt.valueOf() > Date.now()) {
      toast.error('Created date cannot be in the future');
      return;
    }
    await onUpload(files, noteValue.trim(), createdAt.valueOf());
  };

  return (
    <div
      className="attachment-upload-modal-root"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            borderBottom: '1px solid #e8e8e8',
            paddingBottom: '10px',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: 0,
              width: '30px',
              height: '30px',
            }}
          >
            ×
          </button>
        </div>

        <Spin spinning={loading}>
          <div className="mb-3">
            <Upload.Dragger
              fileList={fileList}
              showUploadList
              multiple
              accept={accept}
              listType="picture"
              beforeUpload={beforeUploadValidation}
              onChange={({ fileList: next }) => setFileList(next.slice(0, MAX_UPLOAD_FILES))}
              onRemove={(file) => {
                setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
              }}
              onPreview={() => {}}
            >
              <p className="ant-upload-drag-icon">
                <img src={uploadImageLogo} alt="upload" style={{ width: '120px', height: '120px' }} />
              </p>
              <p className="ant-upload-text alert-link">
                Click or drag file to this area to upload {imagesOnly ? 'image(s)' : 'file(s)'}
              </p>
              <p className="ant-upload-hint">
                Maximum of {MAX_UPLOAD_FILES} {imagesOnly ? 'images' : 'files'} allowed for a description
              </p>
              <p className="ant-upload-hint settings-tr">
                Accepted formats - {imagesOnly ? '.jpeg .png .jpg .webp' : '.jpeg .png .jpg .webp .pdf .doc .docx .xls .xlsx'}
              </p>
            </Upload.Dragger>
          </div>
          <div className="mb-3">
            <label className="dp-upload-field-label">Created On</label>
            <DatePicker
              className="dp-upload-datepicker"
              value={createdAt}
              onChange={(value) => setCreatedAt(value)}
              showTime={{ format: 'HH:mm' }}
              format="MM/DD/YYYY HH:mm"
              allowClear={false}
              disabled={loading}
              disabledDate={disableFutureDate}
              disabledTime={disableFutureTime}
              popupStyle={{ zIndex: 10000000 }}
            />
          </div>
          <Input.TextArea
            placeholder="Add Description..."
            maxLength={constants.attachmentNotesLength}
            showCount
            value={noteValue}
            onChange={(e) => setNoteValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            autoComplete="off"
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={(fileList.length === 0 && !noteValue) || loading}
            >
              Upload Attachment
            </Button>
          </div>
        </Spin>
      </div>
    </div>
  );
};